import { useState, useEffect, useRef, ComponentType, forwardRef, RefObject } from "react";
import { cn } from "@/utils/cn";

interface LazySectionProps {
  /** Factory function that returns a lazy-loaded component */
  component: () => Promise<{ default: ComponentType<any> }>;
  /** Fallback height to prevent layout shift */
  fallbackHeight?: string;
  /** Root margin for intersection observer (load before entering viewport) */
  rootMargin?: string;
  /** Props to pass to the lazy-loaded component */
  componentProps?: Record<string, any>;
  /** External ref to expose the section element */
  sectionRef?: RefObject<HTMLElement>;
}

/**
 * Skeleton placeholder with shimmer animation
 */
const SectionSkeleton = ({ height }: { height: string }) => (
  <div 
    className="w-full overflow-hidden"
    style={{ minHeight: height }}
  >
    <div className="container mx-auto px-6 py-16">
      {/* Title skeleton */}
      <div className="flex flex-col items-center mb-12">
        <div 
          className={cn(
            "h-10 w-64 rounded-lg bg-muted/30",
            "animate-pulse"
          )}
        />
        <div 
          className={cn(
            "h-5 w-96 max-w-full rounded-lg bg-muted/20 mt-4",
            "animate-pulse"
          )}
          style={{ animationDelay: "150ms" }}
        />
      </div>
      
      {/* Content skeleton grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[0, 1, 2].map((i) => (
          <div 
            key={i}
            className={cn(
              "h-48 rounded-xl bg-muted/20",
              "animate-pulse"
            )}
            style={{ animationDelay: `${(i + 2) * 100}ms` }}
          />
        ))}
      </div>
    </div>
  </div>
);

/**
 * LazySection uses Intersection Observer to defer loading components
 * until they're about to enter the viewport. This reduces initial JS
 * execution and improves Time to Interactive (TTI).
 */
export const LazySection = ({
  component,
  fallbackHeight = "300px",
  rootMargin = "200px",
  componentProps = {},
  sectionRef,
}: LazySectionProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isObserverReady, setIsObserverReady] = useState(false);
  const [LazyComponent, setLazyComponent] = useState<ComponentType<any> | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Delay observer activation to ensure LCP completes first
  // This prevents below-fold components from loading during critical render path
  useEffect(() => {
    const handleReady = () => {
      // Wait 3s after page load to start observing
      setTimeout(() => setIsObserverReady(true), 3000);
    };

    if (document.readyState === 'complete') {
      handleReady();
    } else {
      window.addEventListener('load', handleReady, { once: true });
    }

    return () => window.removeEventListener('load', handleReady);
  }, []);

  useEffect(() => {
    if (!isObserverReady) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [rootMargin, isObserverReady]);

  useEffect(() => {
    if (isVisible && !LazyComponent) {
      const loadComponent = async () => {
        const module = await component();
        setLazyComponent(() => module.default);
      };
      loadComponent();
    }
  }, [isVisible, component, LazyComponent]);

  // Not yet visible - render invisible placeholder for intersection observer
  if (!isVisible) {
    return <div ref={ref} style={{ minHeight: fallbackHeight }} />;
  }

  // Visible but still loading - show skeleton
  if (!LazyComponent) {
    return <SectionSkeleton height={fallbackHeight} />;
  }

  // Loaded - render with fade-in animation
  // Pass the sectionRef to the component if provided
  return (
    <div className="animate-fade-in">
      <LazyComponent {...componentProps} ref={sectionRef} />
    </div>
  );
};